import React, { useContext, useEffect, useState } from "react"
import { FormContext } from "~/contexts/FormContext"
import classNames from "../../../../utils/classNames"
import { formatMoney } from "~/utils/formatMoney"
import { AdminContext } from "~/contexts/AdminContext"
import { useDebouncedCallback } from "use-debounce"

interface ComponentProps {
  field: string
  label?: string
  prefix?: string
  suffix?: string
  suffixButtonCta?: string | JSX.Element
  placeholder?: string
  onSuffixButtonClick?: () => void
  className?: string
  hidden?: boolean
  disabled?: boolean
  stopPropagation?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void
  asText?: boolean
  isIntegerOnly?: boolean
  min?: number
  max?: number
}

const NumberInput = ({
  field,
  label,
  prefix,
  suffix,
  onSuffixButtonClick,
  suffixButtonCta,
  placeholder,
  className,
  hidden,
  disabled,
  stopPropagation,
  onChange,
  onInput,
  asText,
  isIntegerOnly,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
}: ComponentProps) => {
  const { resource, register, isChanged, watch, errors, clearError, setValue } = useContext(FormContext)

  const watchedValue = watch(field)

  const { camp } = useContext(AdminContext)
  const [inputValue, setInputValue] = useState<string>(watchedValue || "")

  const { onChange: registeredOnChange, ref: ref, ...registered } = register(field)

  const debounceUpdateValue = useDebouncedCallback((e, parsedValue) => {
    setValue(field, parsedValue)
    registeredOnChange(e)
  }, 300)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isIntegerOnly && [".", ","].includes(e.key)) {
      e.preventDefault()
    }
    if (e.key === " " || e.code === "Space") {
      e.preventDefault()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value

    // Remove multiple leading zeros, but keep single zero and decimal parts
    if (value.match(/^0[0-9]/)) {
      value = value.replace(/^0+/, '0')
    }

    if (value !== "." && value !== "-" && !(Number(value) <= max && Number(value) >= min)) return

    setInputValue(value)

    if (value !== "-") {
      if (errors[field]) clearError(field)

      if (onChange) onChange(e)

      let parsedValue = value === "" ? null : parseFloat(value)

      if (parsedValue !== null) {
        if (parsedValue < min) parsedValue = min
        if (parsedValue > max) parsedValue = max
      }

      debounceUpdateValue(e, parsedValue)
    }
  }

  useEffect(() => {
    setInputValue(watchedValue || "")
  }, [watchedValue])

  if (asText) {
    return (
      <div className={classNames(className, "flex items-center", hidden ? "hidden" : "")}>
        {prefix ? formatMoney(watchedValue, camp?.currency) : watchedValue}
      </div>
    )
  }

  return (
    <div className={classNames(className, hidden && "hidden", disabled && "opacity-30")}>
      {label && (
        <label
          htmlFor={field}
          className={classNames("mb-1 block text-md font-medium", errors[field] ? "text-red-600" : "text-gray-900")}
        >
          {label}
          {errors[field] && <span className="ml-1 text-red-500 text-xs">{errors[field].message}</span>}
        </label>
      )}
      <div className="relative flex flex-grow items-stretch shadow-sm">
        {prefix && (
          <div className="pointer-events-none absolute inset-y-0 left-0 top-0.5 flex items-center pl-3">
            <span className="text-gray-500 sm:text-sm">{prefix}</span>
          </div>
        )}
        <input
          {...registered}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onInput={onInput}
          type="text"
          placeholder={placeholder}
          hidden={!!hidden}
          min={min}
          max={max}
          value={inputValue}
          defaultValue={inputValue}
          onClick={(e) => stopPropagation && e.stopPropagation()}
          disabled={disabled}
          step={isIntegerOnly ? "1" : "any"}
          className={classNames(
            "block w-full",
            prefix && "pl-6",
            suffix && "md:pr-12",
            "rounded-md focus:border-green-600 focus:ring-green-600",
            "disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500",
            errors[field] ? "border-red-600" : "border-gray-300",
            isChanged(field) ? "bg-blue-50" : "bg-white",
          )}
        />
        {suffix && (
          <div
            className="hidden md:flex pointer-events-none absolute inset-y-0 items-center pr-3 top-0.5"
            style={{ right: suffixButtonCta && onSuffixButtonClick ? "7rem" : "0rem" }}
          >
            <span className="text-gray-500 sm:text-sm" id="price-currency">
              {suffix}
            </span>
          </div>
        )}
        {onSuffixButtonClick && suffixButtonCta && (
          <button
            type="button"
            className="relative w-44 -ml-px inline-flex justify-center items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 focus:border-green-600 focus:ring-green-600 focus:ring-1"
            onClick={onSuffixButtonClick}
          >
            {suffixButtonCta}
          </button>
        )}
      </div>
    </div>
  )
}

export default NumberInput
